import { useAtomValue } from "jotai";
import { useQuery } from "convex/react";
import type { ReactNode } from "react";

import { api } from "../../../convex/_generated/api";
import { bootstrapStatusAtom } from "@/core/state";
import { AuthLoadingScreen } from "./AuthLoadingScreen";
import { OnboardingScreen } from "./OnboardingScreen";
import { useSession } from "./useSession";

/**
 * Gate post-auth qui décide quel écran montrer :
 *
 *   bootstrap encore en cours                       → AuthLoadingScreen
 *   superadmin Dozka                                → children (bypass complet du wizard,
 *                                                     l'agence parent Dozka EST celle qui
 *                                                     reçoit les 20% commission)
 *   bootstrap done + accounts === []                → OnboardingScreen (agency step)
 *   bootstrap done + accounts.length>0
 *     ET onboardingCompletedAt absent               → OnboardingScreen (reprend l'étape agence)
 *     ET onboardingCompletedAt présent              → children (cockpit shell)
 *   bootstrap "error"                               → on laisse passer pour ne pas
 *                                                     bloquer un user invité dont le
 *                                                     webhook a déjà créé la membership
 *
 * Source : `accounts.listMine` + `getMyOnboardingState` sont réactifs — dès
 * que `createMyOrganization` insère la row ET que `markOnboardingCompleted`
 * flag l'account, ce gate re-render et bascule sur les children.
 *
 * Dropp.fans n'est PLUS un critère ici (refonte 2026-05-30) : l'accès au
 * cockpit ne dépend plus de la commission 20%. La connexion Dropp n'est plus
 * non plus un prérequis à la création d'un créateur (usage CRM-only possible) ;
 * les 20% ne gatent que l'agent Cyrano (offre de contenu payant), côté backend.
 */
export function OrgGate({ children }: { children: ReactNode }) {
  const status = useAtomValue(bootstrapStatusAtom);
  const accounts = useQuery(api.accounts.listMine);
  const { user } = useSession();

  // Tant qu'on n'a pas la réponse OU que bootstrap tourne, on garde le
  // loader pour ne pas flasher l'onboarding à un user invité.
  if (accounts === undefined) return <AuthLoadingScreen />;
  if (status === "running" || status === "idle") return <AuthLoadingScreen />;

  // Superadmin Dozka : pas d'onboarding wizard, le shell est ouvert d'emblée.
  // Pas de Dropp à configurer (Dozka est l'agence parent qui REÇOIT les 20%),
  // pas de gate "creators-required" (le shell tape `listAllForSuperadmin`
  // cross-account). Source de vérité = session Cyrano (CSV env
  // `cyrano_superadmin_emails`), pas Convex `user.role` qui n'est pas
  // auto-synchronisé.
  if (user?.isSuperadmin && accounts.length > 0) {
    return <>{children}</>;
  }

  if (accounts.length === 0) return <OnboardingScreen />;

  const firstAccount = accounts[0]!;
  if (!firstAccount.onboardingCompletedAt) return <OnboardingScreen />;

  return <>{children}</>;
}
